// src/pages/pengamatan/buah-alami/DownloadFormDialog.jsx
import React from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  AppBar,
  Toolbar,
  Slide,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FormBuahAlami from './FormBuahAlami';
import { useFormData } from './hooks/useFormData';
import { 
  generateBuahAlamiPDF,
  validateDataForPDF 
} from './utils/pdfGenerator';

// Transition untuk full-screen dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Komponen Dialog untuk Download Form
export const DownloadFormDialog = ({
  open,
  selectedLocation,
  searchTerm,
  filteredLocations,
  selectedPengamatanData,
  loading,
  onClose,
  onSearchChange,
  handleAutocompleteChange
}) => {
  
  // State untuk loading generate PDF
  const [generatingPDF, setGeneratingPDF] = React.useState(false);
  const [canClose, setCanClose] = React.useState(true);

  // Gunakan hook untuk mengisi data ke form
  useFormData(selectedPengamatanData);

  // Effect untuk mengontrol apakah dialog bisa ditutup
  React.useEffect(() => {
    setCanClose(!generatingPDF);
  }, [generatingPDF]);

  // Fungsi untuk handle close dialog
  const handleClose = (event, reason) => {
    // Mencegah dialog tertutup dengan backdrop click atau ESC key saat generating PDF
    if (!canClose) {
      return;
    }
    
    // Hanya izinkan penutupan melalui tombol close atau setelah proses selesai
    if (reason && reason === 'backdropClick' && generatingPDF) {
      return;
    }
    
    onClose();
  };

  // Fungsi untuk memastikan elemen PDF ada dan siap
  const ensurePDFElementReady = async () => {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 20; // Maksimal 10 detik (20 x 500ms)
      
      const checkElement = () => {
        attempts++;
        const element = document.getElementById('pdf-content');
        
        if (element && element.offsetWidth > 0 && element.offsetHeight > 0) {
          resolve(element);
        } else if (attempts >= maxAttempts) {
          reject(new Error('Elemen pdf-content tidak ditemukan atau tidak terlihat setelah beberapa percobaan'));
        } else {
          setTimeout(checkElement, 500);
        }
      };
      
      checkElement();
    });
  };

  // Fungsi untuk handle download PDF
  const handleDownload = async () => {
    if (!selectedPengamatanData) {
      alert('Tidak ada data yang dipilih');
      return;
    }

    // BLOCK DIALOG CLOSURE
    setGeneratingPDF(true);
    setCanClose(false);

    try {
      if (!validateDataForPDF(selectedPengamatanData)) {
        alert('Data tidak lengkap untuk generate PDF');
        return;
      }
      
      // Tunggu lebih lama untuk memastikan form ter-render sempurna
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Pastikan elemen PDF ready sebelum generate
      await ensurePDFElementReady();
      
      // Tunggu lagi untuk memastikan data terisi sempurna
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Generate PDF - fungsi ini TIDAK BOLEH membuat dialog tertutup
      await generateBuahAlamiPDF(selectedPengamatanData);
      
      // Beri feedback sukses menggunakan toast (asumsi ada fungsi toast)
      if (typeof window.showToast === 'function') {
        window.showToast('PDF berhasil diunduh!', 'success');
      }
      
    } catch (error) {
      console.error('❌ Error generate PDF:', error);
      alert(`Gagal generate PDF: ${error.message}\n\nPastikan form terlihat di layar dan coba lagi.`);
    } finally {
      // UNBLOCK DIALOG CLOSURE - hanya setelah proses selesai
      setGeneratingPDF(false);
      setCanClose(true);
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose} // Gunakan handleClose yang dikontrol
      TransitionComponent={Transition}
      disableEscapeKeyDown={!canClose} // Blok ESC key saat generating
      hideBackdrop={!canClose} // Non-aktifkan backdrop click saat generating
    >
      {/* TOOLBAR DEFAULT - TIDAK BERUBAH SAAT DOWNLOAD */}
      <AppBar sx={{ position: 'relative', bgcolor: 'primary.main' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
            disabled={!canClose}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ ml: 2, flex: 1 }}>
            <Typography variant="h6" component="div">
              Download Form Pengamatan Buah Alami
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                opacity: 0.9, 
                mt: 0.5,
                color: '#ffffff'
              }}
            >
              {selectedLocation && selectedPengamatanData 
                ? `${selectedPengamatanData.plot ? Object.keys(selectedPengamatanData.plot).length : 0} Plot lokasi ${selectedLocation} berhasil dimuat.`
                : "Pilih lokasi dari dropdown untuk memuat dan melihat pratinjau data."
              }
            </Typography>
          </Box>
          <IconButton 
            color="inherit" 
            onClick={handleDownload}
            disabled={!selectedLocation || !selectedPengamatanData || loading || generatingPDF}
            sx={{
              bgcolor: '#ed6c02',
              '&:hover': {
                bgcolor: '#c95a00ff',
              },
              '&:disabled': {
                bgcolor: 'grey.400',
              },
              width: 40,
              height: 40
            }}
            title="Download PDF"
          >
            <DownloadIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent sx={{ 
        p: 3,
        position: 'relative'
      }}>
        
        {/* OVERLAY LOCK SAAT GENERATING PDF - MENUTUPI SELURUH DIALOG TERMASUK TOOLBAR */}
        {generatingPDF && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              flexDirection: 'column',
              color: 'white'
            }}
          >
            <CircularProgress 
              size={60} 
              thickness={4}
              sx={{ 
                color: 'white',
                mb: 3 
              }} 
            />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
              SEDANG GENERATE PDF
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Harap tunggu sampai proses selesai...
            </Typography>
            <Typography variant="body1" sx={{ textAlign: 'center', maxWidth: '80%' }}>
              Dialog ini <strong>tidak dapat ditutup</strong> selama proses generate PDF berlangsung.
              <br />
              Hal ini untuk mencegah kegagalan dalam pembuatan file PDF.
            </Typography>
          </Box>
        )}

        {/* LOADING STATE */}
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 4 }}>
            <Typography>Memuat data dari database...</Typography>
          </Box>
        )}

        {/* AUTOCOMPLETE SECTION */}
        {!loading && (
          <Box sx={{ mb: 3 }}>
            <Autocomplete
              fullWidth
              freeSolo
              options={filteredLocations || []}
              value={selectedLocation}
              onChange={handleAutocompleteChange}
              inputValue={searchTerm}
              onInputChange={onSearchChange}
              disabled={loading || generatingPDF}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.lokasi;
              }}
              isOptionEqualToValue={(option, value) => {
                if (typeof value === 'string') {
                  return option.lokasi === value;
                }
                return option.lokasi === value.lokasi;
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cari atau Pilih Lokasi"
                  placeholder={loading ? "Memuat data..." : "Ketik untuk mencari atau pilih dari daftar..."}
                  variant="outlined"
                  size="medium"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon color="primary" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} style={{ padding: '8px 16px' }}>
                  <Box sx={{ width: '100%' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                      {option.tanggal_pengamatan}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {option.lokasi}
                    </Typography>
                  </Box>
                </li>
              )}
              ListboxProps={{
                style: {
                  maxHeight: '300px'
                }
              }}
              noOptionsText={
                <Typography color="text.secondary">
                  {searchTerm ? 'Lokasi tidak ditemukan' : 'Tidak ada lokasi tersedia'}
                </Typography>
              }
            />
          </Box>
        )}

        {/* Tampilkan FormBuahAlami hanya jika ada data yang dipilih */}
        {selectedPengamatanData && (
          <Box sx={{ mt: 4, border: '1px solid #ddd', borderRadius: 2, p: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Pratinjau Formulir Data Pengamatan Buah Alami Lokasi {selectedPengamatanData.lokasi}
            </Typography>
            <FormBuahAlami />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DownloadFormDialog;